import * as React from 'react';
import { connect } from 'react-redux';

import { Element } from 'react-scroll';

import {
  createStyles,
  Divider,
  Theme,
  Tooltip,
  Typography,
  WithStyles,
  withStyles,
  Zoom
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { InfoRounded } from '@material-ui/icons';

import { ILieuucation, ILieuucationMapProperties } from '@lieuu/core';

import { IFilterState, IStore } from '@reducers';

import Error from '@components/Error';
import { getNumericalScore, getTextFromScore } from '@components/Map/getScore';

import ExecutiveSummary from './ExecutiveSummary';
import Housing from './Housing';
import MarketProfile from './MarketProfile';
import Safety from './Safety';
import ShortTermRentals from './ShortTermRentals';

import { NavigationDrawerItems } from '../NavigationDrawer';

interface IStoreState {
  filter: IFilterState;
  isDarkMode: boolean;
}

type IProps = IStoreState &
  WithStyles<typeof styles> & {
    isExampleReport: boolean;
    lieuucation: ILieuucation;
  };

const styles = (theme: Theme) =>
  createStyles({
    reportContainer: {
      width: '100%',
      flexGrow: 1,
      flexShrink: 1,
      marginTop: theme.spacing(6),
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    intro: {
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: theme.spacing(2)
    },
    cbsaName: {
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '100%'
    },
    section: {
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: theme.spacing(2)
    },
    divider: {
      marginTop: theme.spacing(2)
    },
    logoImage: {
      height: theme.spacing(9),
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: '50%',
      backgroundColor: grey[900],
      boxShadow: '4px 4px 2px #ccc',
      '-moz-box-shadow': '4px 4px 2px #ccc',
      '-webkit-box-shadow': '4px 4px 2px #ccc',
      '-khtml-box-shadow': '4px 4px 2px #ccc'
    },
    printingTitle: {
      width: '100%',
      justifyContent: 'center',
      display: 'flex'
    },
    printingDivider: {
      height: theme.spacing(5)
    },

    blurred: {
      filter: 'blur(13px)',
      userSelect: 'none'
    },
    scoreContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    score: {
      marginRight: theme.spacing(1)
    }
  });

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.grey[700]
  }
}))(Tooltip);

const ScoringInfoBubble: React.FC<{ score: number }> = ({ score }) => (
  <LightTooltip
    leaveTouchDelay={5000}
    enterTouchDelay={0}
    arrow
    TransitionComponent={Zoom}
    placement="top"
    title={
      <>
        <Typography variant="caption" color="inherit">
          {
            'Scoring is based upon the filters you defined when searching on the map page.'
          }

          <b>
            {(score === 0 || score === -1) &&
              ' A score of 0% indicates that the Lieuucation does not fit the criteria you have set.'}
          </b>
        </Typography>
      </>
    }
  >
    <InfoRounded />
  </LightTooltip>
);

const Details: React.FC<IProps> = ({
  lieuucation,
  filter: { filters },
  classes,
  isExampleReport
}) => {
  const isSmall = false;

  const { name, properties, featureCollection, centLat, centLon } = lieuucation;

  const cbsa = lieuucation.lieuucationCbsa;
  // const zip = lieuucation.lieuucationZip;
  // const country = lieuucation.lieuucationCountry;

  if (
    !cbsa ||
    typeof cbsa === 'string' ||
    !cbsa.properties ||
    !centLon ||
    !centLat
  ) {
    console.error('CBSA was null');
    return <Error />; // handle this better
  }
  // if (!zip || typeof zip === 'string' || !zip.properties) {
  //   console.error('zip was null');
  //   return <Error />; // handle this better
  // }
  // if (!country || typeof country === 'string' || !country.properties) {
  //   console.error('country was null');
  //   return <Error />; // handle this better
  // }

  if (!featureCollection.features[0].properties) {
    console.error('Map props was null');
    return <Error />; // handle this better
  }

  const cbsaGeometryProperties = cbsa.properties;
  // const zipGeometryProperties = zip.properties;
  // const countryGeometryProperties = country.properties;

  const score = isExampleReport
    ? 0.82
    : getNumericalScore(
        featureCollection.features[0].properties as ILieuucationMapProperties,
        filters
      );
  const scoreString = getTextFromScore(score);

  const headingVariant = isSmall ? 'h5' : 'h4';

  return (
    <div className={classes.reportContainer}>
      <Element name="execSummary" className="element">
        <div className={classes.intro}>
          <Typography
            id="lieuucation-title"
            align="center"
            variant={isSmall ? 'h4' : 'h3'}
          >
            {name}
          </Typography>
          <Typography align="center" variant="subtitle1">
            {cbsa.name}
          </Typography>

          <div className={classes.scoreContainer}>
            <Typography className={classes.score} align="center" variant="h6">
              {'Score: '}
              {scoreString}
            </Typography>
            <ScoringInfoBubble score={score} />
          </div>

          <Divider />
        </div>

        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Executive Summary
          </Typography>
          {properties && cbsaGeometryProperties && (
            <ExecutiveSummary
              score={score}
              cbsaProperties={cbsaGeometryProperties}
              // countryProperties={countryGeometryProperties}
              // zipProperties={zipGeometryProperties}
              properties={properties}
            />
          )}

          <Divider className={classes.divider} />
        </section>
      </Element>
      <Element name={NavigationDrawerItems.HOUSING} className="element">
        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Housing
          </Typography>
          {properties.housing && cbsaGeometryProperties.housing && (
            <Housing
              cbsaProperties={cbsaGeometryProperties.housing}
              properties={properties.housing}
              centLat={centLat}
              centLon={centLon}
            />
          )}

          <Divider className={classes.divider} />
        </section>
      </Element>
      <Element name={NavigationDrawerItems.MARKET_PROFILE} className="element">
        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Market Profile
          </Typography>
          {properties.marketProfile && cbsaGeometryProperties.marketProfile && (
            <MarketProfile
              cbsaProperties={cbsaGeometryProperties.marketProfile}
              properties={properties.marketProfile}
            />
          )}

          <Divider className={classes.divider} />
        </section>
      </Element>
      <Element
        name={NavigationDrawerItems.SHORT_TERM_RENTALS}
        className="element"
      >
        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Short Term Rentals
          </Typography>
          {properties.shortTermRentals &&
          properties.shortTermRentals.airbnb &&
          properties.shortTermRentals.airbnb.nightly &&
          !(
            properties.shortTermRentals.airbnb.nightly.roomRate <= 0 &&
            properties.shortTermRentals.airbnb.nightly.wholeHomeRate <= 0
          ) &&
          cbsaGeometryProperties.shortTermRentals ? (
            <ShortTermRentals
              cbsaProperties={cbsaGeometryProperties.shortTermRentals}
              properties={properties.shortTermRentals}
            />
          ) : (
            <Typography gutterBottom align="center" variant="h6">
              There are currently no Airbnb listings in this area!
            </Typography>
          )}

          <Divider className={classes.divider} />
        </section>
      </Element>
      {/* <Element name={NavigationDrawerItems.SCHOOLS} className="element">
        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Schools
          </Typography>
          {properties.schools && cbsaGeometryProperties.schools ? (
            <Schools
              cbsaProperties={cbsaGeometryProperties.schools}
              properties={properties.schools}
            />
          ) : (
            <Typography gutterBottom align="center" variant="h6">
              There is currently no school data for this area!
            </Typography>
          )}

          <Divider className={classes.divider} />
        </section>
      </Element> */}
      <Element name={NavigationDrawerItems.SAFETY} className="element">
        <section className={classes.section}>
          <Typography gutterBottom align="center" variant={headingVariant}>
            Safety
          </Typography>
          {properties.safety && cbsaGeometryProperties.safety ? (
            <Safety
              cbsaProperties={cbsaGeometryProperties.safety}
              properties={properties.safety}
            />
          ) : (
            <Typography gutterBottom align="center" variant="h6">
              There were no crimes in this area during the past month.
            </Typography>
          )}
        </section>
      </Element>
    </div>
  );
};

export default connect(
  (state: IStore): IStoreState => ({
    isDarkMode: state.user.isDarkMode,
    filter: state.filter
  })
)(withStyles(styles)(Details));
